import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const INITIAL_QUESTION = { text: '', options: ['', '', '', ''], correctAnswer: 0 };

const CreateExam = () => {
  const [title, setTitle] = useState('');
  const [duration, setDuration] = useState(30);
  const [questions, setQuestions] = useState([{ ...INITIAL_QUESTION }]);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const addQuestion = useCallback(() => {
    setQuestions(prev => [...prev, { ...INITIAL_QUESTION }]);
  }, []);

  const removeQuestion = useCallback((idx) => {
    setQuestions(prev => {
      if (prev.length <= 1) {
        alert('You must have at least one question.');
        return prev;
      }
      return prev.filter((_, i) => i !== idx);
    });
  }, []);

  const updateQuestionText = useCallback((idx, text) => {
    setQuestions(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], text };
      return next;
    });
  }, []);

  const updateOption = useCallback((qIdx, oIdx, value) => {
    setQuestions(prev => {
      const next = [...prev];
      const q = { ...next[qIdx] };
      q.options = [...q.options];
      q.options[oIdx] = value;
      next[qIdx] = q;
      return next;
    });
  }, []);

  const setCorrectAnswer = useCallback((qIdx, oIdx) => {
    setQuestions(prev => {
      const next = [...prev];
      next[qIdx] = { ...next[qIdx], correctAnswer: oIdx };
      return next;
    });
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post('https://back.kairaablockchainacademy.org/api/exam', {
        title,
        duration: Number(duration),
        questions: questions.map(q => ({
          text: q.text,
          options: q.options,
          correct: q.correctAnswer
        }))
      });
      setSuccess(true);
      setTitle('');
      setDuration(30);
      setQuestions([{ ...INITIAL_QUESTION }]);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      alert('Failed to create exam: ' + (err.response?.data?.error || err.message));
    } finally {
      setSubmitting(false);
    }
  }, [title, duration, questions]);

  return (
    <div style={styles.wrapper}>
      <div style={styles.container}>
        <div style={styles.nav}>
          <Link to="/admin" style={styles.backLink}>← Back to Dashboard</Link>
        </div>

        <h2 style={styles.title}>Create New Exam</h2>

        {success && (
          <div style={styles.successBanner}>Exam Created Successfully!</div>
        )}

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formRow}>
            <div style={{ flex: 1 }}>
              <label style={styles.label}>Exam Title</label>
              <input type="text" required value={title}
                onChange={e => setTitle(e.target.value)}
                style={styles.input} placeholder="e.g. Midterm Exam 2026" />
            </div>
            <div style={{ width: 160 }}>
              <label style={styles.label}>Duration (Mins)</label>
              <input type="number" required min={1} value={duration}
                onChange={e => setDuration(e.target.value)}
                style={styles.input} />
            </div>
          </div>

          <h3 style={styles.questionsTitle}>Questions</h3>

          {questions.map((q, qIdx) => (
            <div key={qIdx} style={styles.questionCard}>
              <div style={styles.qHeader}>
                <strong>Question {qIdx + 1}</strong>
                <button type="button" onClick={() => removeQuestion(qIdx)}
                  style={styles.deleteBtn}>Delete</button>
              </div>

              <textarea required value={q.text}
                onChange={e => updateQuestionText(qIdx, e.target.value)}
                style={styles.textarea} placeholder="Enter question text..." rows={2} />

              <div style={styles.optionsGrid}>
                {q.options.map((opt, oIdx) => (
                  <div key={oIdx} style={{
                    ...styles.optionRow,
                    background: q.correctAnswer === oIdx ? '#e6ffe6' : 'white',
                    borderColor: q.correctAnswer === oIdx ? '#4cbb4c' : '#ccc'
                  }}>
                    <input type="radio" name={`correct-${qIdx}`}
                      checked={q.correctAnswer === oIdx}
                      onChange={() => setCorrectAnswer(qIdx, oIdx)}
                      style={{ marginRight: 10 }} />
                    <input type="text" required value={opt}
                      onChange={e => updateOption(qIdx, oIdx, e.target.value)}
                      style={styles.optInput}
                      placeholder={`Option ${oIdx + 1}`} />
                  </div>
                ))}
              </div>
              <small style={{ color: '#888' }}>* Select the radio button next to the correct option.</small>
            </div>
          ))}

          <div style={styles.actions}>
            <button type="button" onClick={addQuestion} style={styles.addBtn}>
              + Add Question
            </button>
            <button type="submit" disabled={submitting} style={{
              ...styles.saveBtn,
              opacity: submitting ? 0.7 : 1
            }}>
              {submitting ? 'Saving...' : 'Save Exam to Database'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  wrapper: {
    minHeight: '100vh', background: '#f5f5f5', padding: '40px 24px',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
  },
  container: { maxWidth: 800, margin: '0 auto' },
  nav: { marginBottom: 24 },
  backLink: {
    color: '#4cbb4c', textDecoration: 'none', fontSize: 14, fontWeight: 600
  },
  title: {
    margin: '0 0 24px', fontSize: 26, fontWeight: 800, color: '#111'
  },
  successBanner: {
    padding: '12px 16px', background: '#4cbb4c', color: 'white',
    borderRadius: 8, marginBottom: 20, fontWeight: 600, fontSize: 14
  },
  form: {
    background: 'white', padding: '28px', borderRadius: 12,
    boxShadow: '0 1px 4px rgba(0,0,0,.06)'
  },
  formRow: {
    display: 'flex', gap: 20, marginBottom: 24
  },
  label: {
    display: 'block', fontWeight: 700, color: '#333',
    marginBottom: 6, fontSize: 14
  },
  input: {
    width: '100%', padding: '10px 12px', borderRadius: 8,
    border: '1px solid #ddd', fontSize: 14, outline: 'none',
    boxSizing: 'border-box', marginTop: 4,
    transition: 'border-color .2s'
  },
  questionsTitle: {
    fontSize: 18, fontWeight: 700, color: '#111',
    margin: '0 0 16px', paddingBottom: 12,
    borderBottom: '2px solid #f0f0f0'
  },
  questionCard: {
    border: '1px solid #e0e0e0', padding: 20,
    marginBottom: 20, borderRadius: 10, background: '#fafafa'
  },
  qHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 12
  },
  deleteBtn: {
    background: '#fdecea', color: '#c62828', border: 'none',
    padding: '6px 14px', borderRadius: 6, cursor: 'pointer',
    fontWeight: 600, fontSize: 13
  },
  textarea: {
    width: '100%', padding: '10px 12px', borderRadius: 8,
    border: '1px solid #ddd', fontSize: 14, resize: 'vertical',
    boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit'
  },
  optionsGrid: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 12
  },
  optionRow: {
    display: 'flex', alignItems: 'center', padding: '8px 10px',
    border: '1px solid #ccc', borderRadius: 6, transition: 'all .2s'
  },
  optInput: {
    width: '100%', border: 'none', outline: 'none',
    fontSize: 14, background: 'transparent'
  },
  actions: {
    display: 'flex', gap: 12, marginTop: 24, flexWrap: 'wrap'
  },
  addBtn: {
    padding: '10px 20px', background: '#f5f5f5', border: '1px solid #ddd',
    borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14,
    color: '#555', transition: 'all .2s'
  },
  saveBtn: {
    padding: '10px 24px', background: '#28a745', color: 'white',
    border: 'none', borderRadius: 8, cursor: 'pointer',
    fontWeight: 700, fontSize: 14, transition: 'all .2s'
  }
};

export default CreateExam;
